import { useContent } from "../content/ContentContext";
import { formatDate } from "../utils/format";

/**
 * The single affordance for explaining an unfamiliar Dutch or financial
 * term. Used identically on Fast Profile questions/options, `Improve my
 * estimates` questions/subfields, and plan item cards.
 *
 * A native `<details>`/`<summary>` — never a tooltip, popover, modal,
 * `title` attribute, hover target or JS-positioned overlay. Native `details`
 * is keyboard-operable with no code, announced expanded/collapsed with no
 * ARIA, findable by browser find-in-page, and readable with JavaScript
 * broken. No `role`, `aria-expanded` or `aria-controls` is added; the native
 * element already provides them, and this content is never referenced by
 * any `aria-describedby` — see docs/design/features/W4-01.md.
 *
 * A missing or unresolved key renders nothing: no empty disclosure, no `?`
 * that opens onto nothing.
 */
export function ExplainerDisclosure({
  explainerKey,
  className = "",
}: {
  explainerKey: string | undefined;
  className?: string;
}) {
  const { explainer } = useContent();
  const item = explainer(explainerKey);
  if (!item) return null;

  const summary = item.dutch_term
    ? `What does “${item.label}” mean?`
    : "What does this mean?";

  return (
    <details className={`explainer ${className}`.trim()}>
      <summary>{summary}</summary>
      <div className="explainer__body">
        <p className="explainer__meaning">{item.plain_meaning}</p>
        {item.dutch_term && (
          <div className="explainer__field">
            <p className="explainer__eyebrow">In Dutch</p>
            <p>
              {item.dutch_term}
              {item.dutch_gloss && ` (${item.dutch_gloss})`}
            </p>
          </div>
        )}
        {item.why_we_ask && (
          <div className="explainer__field">
            <p className="explainer__eyebrow">Why we ask</p>
            <p>{item.why_we_ask}</p>
          </div>
        )}
        {item.example && (
          <div className="explainer__field">
            <p className="explainer__eyebrow">For example</p>
            <p>{item.example}</p>
          </div>
        )}
        {item.where_to_find && (
          <div className="explainer__field">
            <p className="explainer__eyebrow">Where to find this</p>
            <p>{item.where_to_find}</p>
          </div>
        )}
        {item.sources && item.sources.length > 0 && (
          <div className="explainer__field">
            <p className="explainer__eyebrow">Official source for this term</p>
            {item.sources.map((source) => (
              <p key={`${source.url}-${source.reviewed_at}`}>
                <a href={source.url} rel="noreferrer">
                  {source.publisher}: {source.title}
                </a>
                <br />
                <small>Reviewed {formatDate(source.reviewed_at)}</small>
              </p>
            ))}
          </div>
        )}
      </div>
    </details>
  );
}

/**
 * Renders a short, capped list of `ExplainerDisclosure`s, in declared order.
 * Used on plan item cards, where up to two explainers may accompany one
 * card — see `docs/design/features/W4-01.md` "Density budget".
 */
export function ExplainerDisclosureList({
  explainerKeys,
  max = 2,
  className,
}: {
  explainerKeys: string[] | undefined;
  max?: number;
  className?: string;
}) {
  const keys = (explainerKeys ?? []).slice(0, max);
  if (keys.length === 0) return null;
  return (
    <>
      {keys.map((key) => (
        <ExplainerDisclosure key={key} explainerKey={key} className={className} />
      ))}
    </>
  );
}
